import React from 'react';
import classNames from 'classnames';

import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import styles from './stepper.module.css';

import iconImageActiveMidnight from './assets/active/dark.png';
import iconImageActiveDaylight from './assets/active/light.png';
import iconImageCompletedMidnight from './assets/completed/dark.png';
import iconImageCompletedDaylight from './assets/completed/light.png';
import iconImageFailedMidnight from './assets/failed/dark.png';
import iconImageFailedDaylight from './assets/failed/light.png';
import iconImagePendingMidnight from './assets/pending/dark.png';
import iconImagePendingDaylight from './assets/pending/light.png';

type StepperStyles = {
    stepper: string;

    step: string;

    stepHead: string;
    stepHeadIcon: string;
    stepHeadLabel: string;
    stepHeadPulse: string;

    stepBody: string;
    stepBodyLine: string;
    stepBodyContent: string;

    statusActive: string;
    statusCompleted: string;
    statusFailed: string;
    statusPending: string;
    statusDisabled: string;

    iconImageActive: string;
    iconImageCompleted: string;
    iconImageFailed: string;
    iconImagePending: string;
};

const themedStyles: ThemedStyles<StepperStyles> = {
    [Theme.DAYLIGHT]: {
        stepper: styles.stepper,

        step: styles.step,

        stepHead: styles.stepHead,
        stepHeadIcon: styles.stepHeadIcon,
        stepHeadLabel: styles.stepHeadLabel,
        stepHeadPulse: styles.stepHeadPulseDaylight,

        stepBody: styles.stepBody,
        stepBodyLine: styles.stepBodyLine,
        stepBodyContent: styles.daylightStepBodyContent,

        statusActive: styles.daylightStatusActive,
        statusCompleted: styles.daylightStatusCompleted,
        statusFailed: styles.daylightStatusFailed,
        statusPending: styles.daylightStatusPending,
        statusDisabled: styles.daylightStatusDisabled,

        iconImageActive: iconImageActiveDaylight,
        iconImageCompleted: iconImageCompletedDaylight,
        iconImageFailed: iconImageFailedDaylight,
        iconImagePending: iconImagePendingDaylight,
    },
    [Theme.MIDNIGHT]: {
        stepper: styles.stepper,

        step: styles.step,

        stepHead: styles.stepHead,
        stepHeadIcon: styles.stepHeadIcon,
        stepHeadLabel: styles.stepHeadLabel,
        stepHeadPulse: styles.stepHeadPulseMidnight,

        stepBody: styles.stepBody,
        stepBodyLine: styles.stepBodyLine,
        stepBodyContent: styles.midnightStepBodyContent,

        statusActive: styles.midnightStatusActive,
        statusCompleted: styles.midnightStatusCompleted,
        statusFailed: styles.midnightStatusFailed,
        statusPending: styles.midnightStatusPending,
        statusDisabled: styles.midnightStatusDisabled,

        iconImageActive: iconImageActiveMidnight,
        iconImageCompleted: iconImageCompletedMidnight,
        iconImageFailed: iconImageFailedMidnight,
        iconImagePending: iconImagePendingMidnight,
    },
};

export type StepLabelProps = {
    children: React.ReactNode;
};
export const StepLabel = ({ children }: StepLabelProps) => {
    return (<>{children}</>);
};

export type StepContentProps = {
    children: React.ReactNode;
};
export const StepContent = ({ children }: StepContentProps) => {
    return (<>{children}</>);
};

export type StepProps = {
    status: 'active' | 'completed' | 'failed' | 'pending' | 'disabled';
    children: (
        React.ReactElement<typeof StepLabel> | [
            React.ReactElement<typeof StepLabel>,
            React.ReactElement<typeof StepContent>,
        ]
    );
    pulse?: boolean;
    className?: string;
    onClick?: () => void;
};
export const Step = ({ status, children, pulse, className, onClick }: StepProps) => {
    const styles = useStyles(themedStyles);
    const [label, content] = Array.isArray(children) ? children : [children, null];

    const rootDivClassName = classNames(className, styles.step, {
        [styles.statusActive]: status === 'active',
        [styles.statusCompleted]: status === 'completed',
        [styles.statusFailed]: status === 'failed',
        [styles.statusPending]: status === 'pending',
        [styles.statusDisabled]: status === 'disabled',
    });

    const iconImgSrc = (
        status === 'active' ? styles.iconImageActive :
        status === 'completed' ? styles.iconImageCompleted :
        status === 'failed' ? styles.iconImageFailed :
        styles.iconImagePending
    );

    const iconImgAlt = (
        status === 'active' ? 'Active Icon' :
        status === 'completed' ? 'Completed Icon' :
        status === 'failed' ? 'Failed Icon' :
        'Pending Icon'
    );

    const stepHeadIconClassName = classNames(styles.stepHeadIcon, {
        [styles.stepHeadPulse]: pulse,
    });

    return (
        <div className={rootDivClassName} onClick={onClick}>
            <div className={styles.stepHead}>
                <img className={stepHeadIconClassName}
                     src={iconImgSrc} alt={iconImgAlt} />
                <div className={styles.stepHeadLabel}>
                    {label}
                </div>
            </div>
            <div className={styles.stepBody}>
                <div className={styles.stepBodyLine} />
                <div className={styles.stepBodyContent}>
                    {content}
                </div>
            </div>
        </div>
    );
};

export type StepperProps = {
    children: (
        React.ReactElement<typeof Step> |
        React.ReactElement<typeof Step>[]
    );
};
export const Stepper = ({ children }: StepperProps) => {
    const styles = useStyles(themedStyles);
    return (
        <div className={styles.stepper}>
            {children}
        </div>
    );
};
